import {
  FormantBotAvatarSchema,
  formantBotAvatarFromSeed,
  type FormantBotAvatar,
} from '@cortex/types';
import { useEffect, useRef } from 'react';

import {
  makeAvatarLayers,
  renderAgentAvatar,
  type AgentAvatarState,
  type AvatarLayers,
} from './agent-avatar';

export type { AgentAvatarState };

/** Product face. `avatar` is kept so callers that still pass a stored trace compile. */
export function BotAvatar({
  size = 22,
  state = 'idle',
  title,
  avatar,
}: {
  size?: number;
  state?: AgentAvatarState;
  title?: string;
  avatar?: FormantBotAvatar;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const layers = useRef<AvatarLayers | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    const t0 = performance.now();
    const frame = (now: number) => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const S = Math.max(48, Math.round(size * dpr * (size < 48 ? 2 : 1)));
      if (!layers.current) layers.current = makeAvatarLayers(S);
      const t = reduce ? 0.4 : ((now - t0) / 1000) % 4;
      renderAgentAvatar(canvas, state, t, S, layers.current, avatar);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [size, state, avatar]);

  return (
    <canvas
      ref={ref}
      className="bot-avatar"
      width={size}
      height={size}
      style={{ width: size, height: size, display: 'block' }}
      role="img"
      aria-label={title ?? 'Bot avatar'}
    />
  );
}

export function resolveBotAvatar(stored: unknown, seed: string): FormantBotAvatar {
  const parsed = FormantBotAvatarSchema.safeParse(stored);
  return parsed.success ? parsed.data : formantBotAvatarFromSeed(seed);
}
